import type { HeatProtectionContactReceptionStatusType, PrismaWhere } from '~/types'
import type { PrismaQueryParameters } from '~/utils/useQueryParams'
import { useQuery } from '@tanstack/vue-query'

export const useHeatProtectionQuery = () => {
  const { $trpc } = useNuxtApp()

  return {
    contactQuery: {
      findMany: (query?: MaybeRef<PrismaQueryParameters<'heatProtectionContact'>>, receptionStatus?: HeatProtectionContactReceptionStatusType) => useQuery({
        queryFn: () => $trpc.heatProtection.contact.findManyHeatProtectionContact.query({
          query: unref(query),
          receptionStatus,
        }),
        queryKey: ['heatProtection', 'contact', 'getAll', query, receptionStatus],
      }),
      findOne: (where?: MaybeRef<PrismaWhere<'heatProtectionContact'>>) => useQuery({
        queryFn: () => $trpc.heatProtection.contact.findOneHeatProtectionContact.query(unref(where)),
        queryKey: ['heatProtection', 'contact', 'getById', where],
        enabled: !!unref(where)?.id,
      }),
      getTimeline: (where?: MaybeRef<PrismaWhere<'heatProtectionContact'>>) => useQuery({
        queryFn: () => $trpc.heatProtection.contact.getHeatProtectionContactTimeline.query(unref(where)),
        queryKey: ['heatProtection', 'contact', 'getById', where, 'timeline'],
        enabled: !!unref(where)?.id,
      }),
      findManyForHeatMap: () => useQuery({
        queryFn: () => $trpc.heatProtection.contact.findManyHeatProtectionContactForHeatMap.query(),
        queryKey: ['heatProtection', 'contact', 'getAll', 'heatMap'],
      }),
    },
    callProtocolQuery: {
      findMany: (query?: MaybeRef<PrismaQueryParameters<'heatProtectionCallProtocol'>>) => useQuery({
        queryFn: () => $trpc.heatProtection.call.findManyHeatProtectionCall.query(unref(query)),
        queryKey: ['heatProtection', 'call', 'getAll', query],
      }),
      findOne: (where?: MaybeRef<PrismaWhere<'heatProtectionCallProtocol'>>) => useQuery({
        queryFn: () => $trpc.heatProtection.call.findOneHeatProtectionCall.query(unref(where)),
        queryKey: ['heatProtection', 'call', 'getById', where],
        enabled: !!unref(where)?.id,
      }),
      findActiveByContact: (contactId: MaybeRef<string>) => useQuery({
        queryFn: () => $trpc.heatProtection.call.findActiveHeatProtectionCallByContactId.query({ id: unref(contactId) }),
        queryKey: ['heatProtection', 'call', 'getActiveByContactId', contactId],
      }),
    },
    warningQuery: {
      findMany: (query?: MaybeRef<PrismaQueryParameters<'heatProtectionWarning'>>) => useQuery({
        queryFn: () => $trpc.heatProtection.warning.findManyHeatProtectionWarning.query(unref(query)),
        queryKey: ['heatProtection', 'warning', 'getAll', query],
      }),
      findOne: (where?: MaybeRef<PrismaWhere<'heatProtectionWarning'>>) => useQuery({
        queryFn: () => $trpc.heatProtection.warning.findOneHeatProtectionWarning.query(unref(where)),
        queryKey: ['heatProtection', 'warning', 'getById', where],
        enabled: !!unref(where)?.id,
      }),
    },
    receptionProtocolQuery: {
      findLastByContactId: (id: MaybeRef<string>) => useQuery({
        queryFn: () => $trpc.heatProtection.receptionProtocol.findHeatProtectionReceptionProtocolByContact.query({ id: unref(id) }),
        queryKey: ['heatProtection', 'reception', 'getByContactId', id],
      }),
      findOne: (id?: MaybeRef<string | undefined>) => useQuery({
        queryFn: () => $trpc.heatProtection.receptionProtocol.findOneHeatProtectionReceptionProtocol.query({ id: unref(id) }),
        queryKey: ['heatProtection', 'reception', 'getById', id],
        enabled: !!unref(id),
      }),
    },
    institutionQuery: {
      findMany: (query?: MaybeRef<PrismaQueryParameters<'heatProtectionInstitution'>>) => useQuery({
        queryFn: () => $trpc.heatProtection.institution.findManyHeatProtectionInstitution.query(unref(query)),
        queryKey: ['heatProtection', 'institution', 'getAll', query],
      }),
      findOne: (where?: MaybeRef<PrismaWhere<'heatProtectionInstitution'>>) => useQuery({
        queryFn: () => $trpc.heatProtection.institution.findOneHeatProtectionInstitution.query(unref(where)),
        queryKey: ['heatProtection', 'institution', 'getById', where],
        enabled: !!unref(where)?.id,
      }),
    },
  }
}
